import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  ColorInput,
  Input,
  Label,
  Separator,
  Spinner,
  ThemePreview,
  useFeatureFlag,
} from '@lensora/ui'
import * as React from 'react'


import { useMe } from '@/hooks/use-me'
import { useTenantConfig } from '@/hooks/use-tenant-config'
import {
  useBrandingMutation,
  useCustomDomainMutation,
  useLogoUploadMutation,
} from '@/hooks/use-tenant-settings'
import { ApiError } from '@/lib/api'

const MAX_LOGO_BYTES = 2 * 1024 * 1024

export function BrandPage() {
  const { data, isLoading } = useMe()
  const { data: tenantConfig } = useTenantConfig()
  const brandingMutation = useBrandingMutation()
  const domainMutation = useCustomDomainMutation()
  const logoUploadMutation = useLogoUploadMutation()
  const customDomainEnabled = useFeatureFlag('custom_domain_enabled')
  const whiteLabelEnabled = useFeatureFlag('white_label_enabled')

  const [logoUrl, setLogoUrl]             = React.useState('')
  const [brandPrimary, setBrandPrimary]   = React.useState('#0f172a')
  const [brandAccent, setBrandAccent]     = React.useState('#6366f1')
  const [customDomain, setCustomDomain]   = React.useState('')
  const [saved, setSaved]                 = React.useState(false)
  const [logoError, setLogoError]         = React.useState<string | null>(null)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const initialisedRef = React.useRef(false)

  React.useEffect(() => {
    const branding = tenantConfig?.tenant.branding
    if (!branding || initialisedRef.current) return
    initialisedRef.current = true

    setLogoUrl(branding.logoUrl ?? '')
    if (branding.brandPrimary) setBrandPrimary(branding.brandPrimary)
    if (branding.brandAccent) setBrandAccent(branding.brandAccent)
  }, [tenantConfig])

  async function handleLogoFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = '' // allow re-selecting the same file later
    if (!file) return

    setLogoError(null)
    if (file.size > MAX_LOGO_BYTES) {
      setLogoError('Logo must be smaller than 2MB.')
      return
    }

    try {
      const updated = await logoUploadMutation.mutateAsync(file)
      setLogoUrl(updated.branding.logoUrl ?? '')
    } catch (err) {
      setLogoError(err instanceof ApiError ? err.message : 'Failed to upload logo. Please try again.')
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  if (!whiteLabelEnabled) {
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-text">Brand settings</h2>
          <p className="mt-1 text-sm text-text-muted">White label branding for your platform.</p>
        </div>
        <Card className="border-feedback-info/30 bg-feedback-info/5">
          <CardContent className="pt-6">
            <p className="text-sm text-text">
              White label branding is available on the Studio plan.{' '}
              <a href="/dashboard/settings/billing" className="text-brand-accent underline">
                Upgrade your plan
              </a>{' '}
              to set your own logo, colours, and custom domain.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  async function handleSaveBranding(e: React.FormEvent) {
    e.preventDefault()
    await brandingMutation.mutateAsync({
      logoUrl: logoUrl || null,
      brandPrimary,
      brandAccent,
    })
    setSaved(true)
    setTimeout(() => setSaved(false), 2500)
  }

  async function handleSaveDomain(e: React.FormEvent) {
    e.preventDefault()
    await domainMutation.mutateAsync(customDomain || null)
  }

  const previewBranding = {
    logoUrl: logoUrl || null,
    brandPrimary,
    // TenantBranding still has this field (DB column not dropped in this
    // change — see MR notes) but it's no longer editable or rendered
    // anywhere, so just pass through whatever's already saved.
    brandSecondary: tenantConfig?.tenant.branding.brandSecondary ?? '',
    brandAccent,
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-text">Brand settings</h2>
        <p className="mt-1 text-sm text-text-muted">
          Customise how your platform looks to your clients.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Branding form */}
        <div className="space-y-6">
          <form onSubmit={handleSaveBranding}>
            <Card>
              <CardHeader>
                <CardTitle>Brand colours</CardTitle>
                <CardDescription>
                  These colours are applied across your entire platform.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="space-y-2">
                  <Label>Logo</Label>
                  <div className="flex items-center gap-3">
                    {logoUrl ? (
                      <img
                        src={logoUrl}
                        alt="Current logo"
                        className="h-12 w-12 rounded border border-border object-contain bg-surface p-1"
                      />
                    ) : (
                      <div className="flex h-12 w-12 items-center justify-center rounded border border-dashed border-border text-[10px] text-text-muted">
                        No logo
                      </div>
                    )}
                    <div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/svg+xml,image/webp"
                        className="hidden"
                        onChange={handleLogoFileChange}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={logoUploadMutation.isPending}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {logoUploadMutation.isPending ? 'Uploading…' : logoUrl ? 'Replace logo' : 'Upload logo'}
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-text-muted">
                    Square image, at least 64×64px. PNG, SVG, JPEG, or WebP — max 2MB.
                  </p>
                  {logoError && <p className="text-xs text-feedback-error">{logoError}</p>}
                </div>

                <Separator />

                <ColorInput
                  label="Primary colour"
                  value={brandPrimary}
                  onValueChange={setBrandPrimary}
                />
                <ColorInput
                  label="Accent colour"
                  value={brandAccent}
                  onValueChange={setBrandAccent}
                />
              </CardContent>
              <CardFooter className="gap-3">
                <Button
                  type="submit"
                  size="sm"
                  disabled={brandingMutation.isPending}
                >
                  {brandingMutation.isPending ? 'Saving…' : saved ? 'Saved!' : 'Save branding'}
                </Button>
                {brandingMutation.isError && (
                  <p className="text-xs text-feedback-error">
                    {brandingMutation.error instanceof Error
                      ? brandingMutation.error.message
                      : 'Failed to save'}
                  </p>
                )}
              </CardFooter>
            </Card>
          </form>

          {/* Custom domain */}
          {customDomainEnabled && (
            <form onSubmit={handleSaveDomain}>
              <Card>
                <CardHeader>
                  <CardTitle>Custom domain</CardTitle>
                  <CardDescription>
                    Point your domain's DNS A record at our servers, then enter it here.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Label>Domain</Label>
                    <Input
                      type="text"
                      placeholder="app.yourbusiness.com"
                      value={customDomain}
                      onChange={(e) => setCustomDomain(e.target.value)}
                    />
                  </div>
                </CardContent>
                <CardFooter>
                  <Button type="submit" size="sm" variant="outline" disabled={domainMutation.isPending}>
                    {domainMutation.isPending ? 'Saving…' : 'Save domain'}
                  </Button>
                </CardFooter>
              </Card>
            </form>
          )}
        </div>

        {/* Live preview */}
        <div className="space-y-3">
          <p className="text-sm font-medium text-text">Live preview</p>
          <ThemePreview
            branding={previewBranding}
            name={data?.tenant?.name ?? 'Your brand'}
          />
          <p className="text-xs text-text-muted">
            Updates as you change colours — save to apply to your platform.
          </p>
        </div>
      </div>
    </div>
  )
}
